import PropTypes from 'prop-types';
import { getIn } from 'formik';
import Button from '@mui/material/Button';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import React from 'react';
import TextField from '@mui/material/TextField';

import InvoiceField from './InvoiceField';
import { openSnackbar } from 'api/snackbar';

export default function InvoiceItem({
  id,
  index,
  itemData,
  onDeleteItem,
  onEditItem,
  Blur,
  errors,
  touched,
  amount,
  fieldsConfig,
  patientSelected,
  wholesalerSelected,
  push,
  variant,
  isCancellationMode = false,
  allInvoiceItems = [],
  salePriceType,
  isEditMode = false,
  requestFocus,
  userDiscount = null,
  hasUserDiscount = false,
  showGst = true
}) {
  const handleModalClose = (status) => {
    if (status) {
      onDeleteItem(index);
      openSnackbar({ open: true, message: 'Item deleted', variant: 'alert', alert: { color: 'success' } });
    }
  };

  return (
    <>
      {fieldsConfig.map((field) => {
        if (!showGst && String(field.key || '').toLowerCase().includes('gst')) {
          return null;
        }
        const path = `invoice_detail[${index}].${field.key}`;
        const error = getIn(errors, path);
        const touch = getIn(touched, path);
        const focusFieldType = field.key === 'name' ? 'medicine' : field.key === 'qty' ? 'quantity' : undefined;
        
        const isCustomPlaceholder = itemData.isCustomPlaceholder && field.key === 'name';
        // Support both customMedicineName (pharmacy) and customTestName (lab)
        const customName = itemData.customTestName || itemData.customMedicineName;
        const customDosage = itemData.customDosage;
        
        return (
          <React.Fragment key={field.key}>
            <InvoiceField
              id={id}
              index={index}
              onEditItem={onEditItem}
              onBlur={Blur}
              cellData={{
                placeholder: field.label,
                name: path,
                type: field.type,
                id: `${id}_${field.key}`,
                value: itemData[field.key],
                errors: error,
                touched: touch,
                label: field.label,
                readOnly: field.readOnly || isCancellationMode,
                fetchOptions: field.fetchOptions,
                disabled: field.disabled,
                showEmpty: field.showEmpty,
                customPlaceholderInfo: isCustomPlaceholder && customName ? {
                  name: customName,
                  dosage: customDosage
                } : null,
                key: field.key,
              }}
              itemData={itemData}
              patientSelected={patientSelected}
              wholesalerSelected={wholesalerSelected}
              push={push}
              variant={variant}
              isCancellationMode={isCancellationMode}
              isEditMode={isEditMode}
              allInvoiceItems={allInvoiceItems}
              salePriceType={salePriceType}
              requestFocus={requestFocus}
              focusFieldType={focusFieldType}
              userDiscount={userDiscount}
              hasUserDiscount={hasUserDiscount}
            />
            {/* Insert Cancel Qty field right after name field in cancellation mode */}
            {isCancellationMode && field.key === 'name' && (
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ min: 0, max: itemData.qty }}
                  placeholder="0"
                  name={`invoice_detail.${index}.cancel_quantity`}
                  value={itemData.cancel_quantity || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const numericValue = parseFloat(inputValue);
                    const maxQuantity = itemData.qty || 0;
                    
                    // If current quantity is zero, disable the field completely
                    if (maxQuantity === 0) {
                      return;
                    }
                    
                    // If input is empty or invalid, allow it (user might be typing)
                    if (inputValue === '' || isNaN(numericValue)) {
                      onEditItem(e);
                      return;
                    }
                    
                    // If typed value exceeds max quantity, auto-correct to max
                    if (numericValue > maxQuantity) {
                      const correctedEvent = {
                        ...e,
                        target: {
                          ...e.target,
                          value: maxQuantity.toString()
                        }
                      };
                      onEditItem(correctedEvent);
                      
                      // Show a brief notification
                      openSnackbar({
                        open: true,
                        message: `Maximum quantity available is ${maxQuantity}`,
                        variant: 'alert',
                        alert: { color: 'warning' }
                      });
                    } else {
                      onEditItem(e);
                    }
                  }}
                  disabled={itemData.qty === 0}
                />
              </TableCell>
            )}
          </React.Fragment>
        );
      })}
      
      {/* Additional fields for cancellation mode */}

      {!isCancellationMode && (
        <TableCell align="center" sx={{ width: 80, verticalAlign: 'top' }}>
          <Tooltip title="Remove Item">
            <Button 
              color="error" 
              onClick={() => handleModalClose(true)}
              size="small"
              variant="text"
              sx={{ 
                minWidth: 'auto', 
                height: '56px', // Match standard TextField height
                width: '40px',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1
              }}
            >
              <DeleteOutlined style={{ fontSize: '20px' }} />
            </Button>
          </Tooltip>
        </TableCell>
      )}
    </>
  );
}

InvoiceItem.propTypes = {
  id: PropTypes.any,
  index: PropTypes.number.isRequired,
  itemData: PropTypes.object.isRequired,
  onDeleteItem: PropTypes.func,
  onEditItem: PropTypes.func,
  Blur: PropTypes.func,
  errors: PropTypes.object,
  touched: PropTypes.object,
  amount: PropTypes.number,
  fieldsConfig: PropTypes.array.isRequired,
  push: PropTypes.func,
  isCancellationMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
  salePriceType: PropTypes.string,
  requestFocus: PropTypes.func,
  userDiscount: PropTypes.number,
  hasUserDiscount: PropTypes.bool,
  showGst: PropTypes.bool
};